import React from 'react';

import { Box, Chip, Skeleton } from '@mui/material';
import { CardBody, CardTitle } from '@apigrowfinance/pbolt-card';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage, useIntl } from 'react-intl';

import { CustomerCard } from '@clients/customersClient';
import BankingCardDecorator from '@components/BankingCardDecorator/BankingCardDecorator';
import CardStatus from '@components/cardStatus/CardStatus';
import cardStatusTranslations from '@messages/cardStatus';
import cardType from '@messages/cardType';
import getEnumTranslation from '@utils/getEnumTranslation';

export interface CardsListRowProps {
    card: CustomerCard;
    isLoading?: boolean;
}

const CardsListRow = ({ card, isLoading = false }: CardsListRowProps) => {
    const { theme } = useTheme();
    const { formatMessage } = useIntl();

    return (
        <CardBody direction="row">
            <Box
                width={235}
                data-testid={`card-row-${card.id}`}
                sx={{ mr: 1.5 }}>
                <BankingCardDecorator card={card} isLoading={isLoading} />
            </Box>

            <Box sx={{ flexGrow: 1, alignSelf: 'center' }}>
                <Chip
                    size="small"
                    {...(card?.type && {
                        label: getEnumTranslation(card.type.toLowerCase(), {
                            messages: cardType,
                            formatMessage,
                        }),
                    })}
                />

                <CardTitle isLoading={isLoading}>{card?.name}</CardTitle>

                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            mr: theme.spacing(2),
                        }}>
                        <Box sx={{ mr: theme.spacing(1) }}>
                            {isLoading ? (
                                <Skeleton width={60} />
                            ) : (
                                <FormattedMessage
                                    id="Cards.status"
                                    defaultMessage="Status"
                                />
                            )}
                        </Box>
                        <CardStatus status={card?.status} isLoading={isLoading}>
                            {getEnumTranslation(card?.status, {
                                messages: cardStatusTranslations,
                            })}
                        </CardStatus>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            mr: theme.spacing(2),
                        }}>
                        {isLoading ? (
                            <Skeleton width={60} />
                        ) : (
                            <>
                                <Box sx={{ mr: theme.spacing(1) }}>
                                    <FormattedMessage
                                        id="Cards.cardId"
                                        defaultMessage="Card ID"
                                    />
                                </Box>
                                {card?.id}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </CardBody>
    );
};

export default CardsListRow;
